import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppButton from '@/components/buttons/AppButton';
import ForgotPasswordForm from "@/components/forms/ForgotPasswordForm";
import FormField from '@/components/forms/FormField';
import CenterConatainer from "@/components/layouts/CenterContainer";
import LogoHeader from "@/components/layouts/LogoHeader";
import { IS_CAPTCHA_ENABLED } from "@/constants/allconstants";
import { useAuth } from "@/context/providers/AuthProvider";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";
import { login, loginWithAzure, msal } from '@/services/auth.service';
import { BUTTONS, HEADINGS, LABELS, PLACEHOLDERS } from "@/utils/constants/appConstants/commonConstants";
import { SIGN_IN_WITH_ENTRA_ID } from "@/utils/constants/appConstants/DirectoryConstants";
import { ERROR_CODES, PORTAL_TYPE } from '@/utils/constants/constants';
import { INVALID_EMAIL_ADDRESS, INVALID_CREDENTIALS_LOGIN_MESSAGE, REPEATED_INVALID_LOGIN_ATTEMPTS_MESSAGE, INVALID_CAPTCHA_TOKEN } from '@/utils/constants/messages';
import { checkSSOLoginAllowed, checkSSOLoginRestricted, isEmail, storeLoginUserData } from '@/utils/functions/helperFunctions';
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { errorToasterMessage, infoToasterMessage } from '@/utils/functions/toaster';
import { getemail_address } from '@/utils/functions/TokenFunction';
import { ReCaptchaV2 } from "@/utils/hooks/RecaptchaV2";
import { useCaptcha } from "@/utils/hooks/useRecaptcha";

type Props = {};

interface CaptchaData {
    captcha_token: string;
    captcha_type: 'V3' | 'V2';
}

function Password({ }: Props) {
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [show, setShow] = useState(false);
    const [password, setPassword] = useState("");
    const [msg, setMsg] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [loaderLoginWithSSO, setLoaderLoginWithSSO] = useState(false);
    const [queryParams, setQueryParams] = useSearchParams();
    const email = getemail_address();
    const loginViaSSO = checkSSOLoginAllowed();
    const isLoginRestrictedViaSSO = checkSSOLoginRestricted();
    const currentOrganization: any = getCurrentOrganization();
    const navigate = useNavigate();
    const { setToken } = useAuth();
    const { setOrganization } = useContext(OrganizationContext);
    const { executeCaptcha, setCaptchaV2Token, captchaV3enabled, setCaptchaV3enabled, captchaV2Token } = useCaptcha({ onV2Failure: () => setMsg(INVALID_CAPTCHA_TOKEN) });

    useEffect(() => {
        if (queryParams.has('code')) {
            setLoaderLoginWithSSO(true);
            const data = { code: queryParams.get('code'), organization: currentOrganization.organization, email };
            loginWithAzure(data, (res: any) => {
                queryParams.delete('code');
                if (queryParams.has('client_info')) queryParams.delete('client_info');
                if (queryParams.has('session_state')) queryParams.delete('session_state');
                setQueryParams(queryParams);
                storeLoginUserData(res.data);
                setToken(res.data.access_token);
                setOrganization(res.data.current_organization);
                localStorage.setItem('loginWithSSO', 'true');
                setLoaderLoginWithSSO(false);
                navigate('/');
            }, () => setLoaderLoginWithSSO(false));
        }
    }, []);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => { setMsg(""); setPassword(e.target.value); };

    const handleLoginApi = (data: any) => {
        login(data, (res: any) => { setIsLoading(false); navigate("/otp"); },
            (err: any) => {
                setIsLoading(false);
                if (err?.response?.status === 403 && err?.response?.data?.error_code === ERROR_CODES.IP_WHITELISTING_ERROR_CODE) infoToasterMessage(err?.response?.data?.message);
                else if (err?.response?.data?.statusCode === 401) setMsg(INVALID_CREDENTIALS_LOGIN_MESSAGE);
                else if (err?.response?.data?.error_code === 527 || err.response?.data?.error_code === 528) setMsg(REPEATED_INVALID_LOGIN_ATTEMPTS_MESSAGE);
                else if (err?.response?.data?.error_code === 618) errorToasterMessage(err?.response?.data?.message);
                else if (err?.response?.data?.error_code === ERROR_CODES.CAPTCHA_V3_INVALID_TOKEN_ERROR_CODE || err.response?.data?.error_code === ERROR_CODES.CAPTCHA_NO_TOKEN_ERROR_CODE) setCaptchaV3enabled(false);
                else setMsg(err?.response?.data?.message);
            }
        );
    };

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        if (!isEmail(email)) { setIsLoading(false); setMsg(INVALID_EMAIL_ADDRESS); return; }
        if (IS_CAPTCHA_ENABLED) {
            try { await executeCaptcha('admin_login_action', (captchaData: CaptchaData) => handleLoginApi(JSON.stringify({ email_address: email, password, organization: currentOrganization.organization, portal_type: "admin", ...captchaData }))); }
            catch (err: any) { setIsLoading(false); setMsg(INVALID_CAPTCHA_TOKEN); }
        } else {
            handleLoginApi(JSON.stringify({ email_address: email, password, organization: currentOrganization.organization, portal_type: "admin" }));
        }
    }, [email, password, setIsLoading]);

    return (
        <CenterConatainer>
            {!(showForgotPassword && forgotPasswordSuccess) && (
                <LogoHeader
                    id={showForgotPassword ? "forgot-password-form" : "login-form"}
                    heading={showForgotPassword ? HEADINGS.FORGOT_PASSWORD : HEADINGS.LOGIN}
                    children={undefined}
                />
            )}
            {!showForgotPassword ? (
                <>
                    {!isLoginRestrictedViaSSO && (
                        <form method="POST" id="signInForm" onSubmit={handleSubmit} autoComplete="off">
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-sm font-medium text-foreground" htmlFor="login-form-password-input" id="login-form-password-label">{LABELS.PASSWORD}</label>
                                    <a className="text-sm text-primary hover:underline cursor-pointer" id="login-form-forgot-password-link" tabIndex={-1} onClick={() => { setShowForgotPassword(true); setShow(true); setShowSuccess(false); setForgotPasswordSuccess(false); }} href="#">{LABELS.FORGOT_PASSWORD}</a>
                                </div>
                                <FormField value={password} onChange={handlePasswordChange} type="password" id="login-form-password-input" placeholder={PLACEHOLDERS.PASSWORD} required />
                            </div>
                            {msg && <p className="text-destructive text-sm mb-3">{msg}</p>}
                            <div>
                                <AppButton id="sign-in-btn" disabled={!captchaV3enabled && !captchaV2Token} isLoading={isLoading} btnText={BUTTONS.SIGN_IN} className="btn-lg btn-block btn-primary" type="submit" />
                            </div>
                        </form>
                    )}
                    {loginViaSSO && (
                        <div className='mt-2'>
                            <AppButton disabled={!captchaV3enabled && !captchaV2Token} icon={<em className='icon ni ni-microsoft mr-2' />} id='sso-login-btn' isLoading={loaderLoginWithSSO} btnText={SIGN_IN_WITH_ENTRA_ID} className={`btn btn-lg ${!isLoginRestrictedViaSSO ? 'btn-outline-primary' : 'btn-primary'} btn-block`} onClick={async () => msal(currentOrganization.organization, email, PORTAL_TYPE.ADMIN, (url: string) => window.location.assign(url))} />
                        </div>
                    )}
                    <div className='mt-2'>{!captchaV3enabled ? <ReCaptchaV2 setToken={setCaptchaV2Token} /> : null}</div>
                </>
            ) : (
                <ForgotPasswordForm onSuccessChange={setForgotPasswordSuccess} />
            )}
        </CenterConatainer>
    );
}

export default Password;
